import { Box, Card, Grid, Stack, Typography, CircularProgress, Alert, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, Paper, TextField, InputAdornment, TablePagination } from "@mui/material";
import { AccountBalance as AccountBalanceIcon, AccountBalanceWallet as AccountBalanceWalletIcon, Payment as PaymentIcon, Search as SearchIcon } from "@mui/icons-material";
import pxToRem from "../../../../Themes/Functions/pxToRem";
import uuid from "react-uuid";
import moment from "moment";
import { noScrollBarStyle } from "../../../../Configurations/AppStyle";
import { useEffect, useMemo, useState } from "react";
import AppService from "../../../../Configurations/Appservice";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";

const CustomerPaymentsPage = () => {
  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useState(0);

  const [organisations, setOrganisations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState("");

  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [overviewData, setOverviewData] = useState({
    totalBankBalance: 0,
    activeBankAccounts: 0,
    paidTicketPaymentsAmount: 0,
    paidTicketCount: 0,
    pendingTicketPaymentsAmount: 0,
    pendingTicketCount: 0,
    inReviewTicketPaymentsAmount: 0,
    inReviewTicketCount: 0,
    totalCustomerWalletBalances: 0,
    customersWithBalance: 0,
  });

  const [topups, setTopups] = useState([]);
  const [ticketPayments, setTicketPayments] = useState([]);
  const [topupsLoading, setTopupsLoading] = useState(false);
  const [ticketPaymentsLoading, setTicketPaymentsLoading] = useState(false);
  const [topupsError, setTopupsError] = useState(null);
  const [ticketPaymentsError, setTicketPaymentsError] = useState(null);

  // Customers tab — server-paginated, ordered by oldest unpaid first.
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [customersPage, setCustomersPage] = useState(0);
  const [customersPageSize, setCustomersPageSize] = useState(20);
  const [customersSearch, setCustomersSearch] = useState("");
  const [customersSearchInput, setCustomersSearchInput] = useState("");

  const formatAmount = (amount) => {
    const value = amount || 0;
    return `AED ${value.toLocaleString("en-AE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    return moment(dateValue).format("YYYY-MM-DD");
  };

  // Only the bank-wallet and ticket-payments sub-sections are needed; calling
  // them in parallel avoids the slow monolithic /overview aggregation.
  const fetchFinanceOverview = async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const [bank, ticketPayments] = await Promise.all([
        AppService.get("v1/admin/finance/overview/bank-wallet").catch(() => null),
        AppService.get("v1/admin/finance/overview/ticket-payments").catch(() => null)
      ]);
      setOverviewData({
        totalBankBalance: bank?.totalBankBalance || 0,
        activeBankAccounts: bank?.activeBankAccounts || 0,
        paidTicketPaymentsAmount: ticketPayments?.paidTicketPaymentsAmount || 0,
        paidTicketCount: ticketPayments?.paidTicketCount || 0,
        pendingTicketPaymentsAmount: ticketPayments?.pendingTicketPaymentsAmount || 0,
        pendingTicketCount: ticketPayments?.pendingTicketCount || 0,
        inReviewTicketPaymentsAmount: ticketPayments?.inReviewTicketPaymentsAmount || 0,
        inReviewTicketCount: ticketPayments?.inReviewTicketCount || 0,
        totalCustomerWalletBalances: bank?.totalCustomerWalletBalances || 0,
        customersWithBalance: bank?.customersWithBalance || 0,
      });
    } catch (err) {
      console.error("Error fetching finance overview:", err);
      setOverviewError("Failed to load finance overview");
    } finally {
      setOverviewLoading(false);
    }
  };

  const fetchCustomerOrganisations = async () => {
    setOrgLoading(true);
    try {
      const response = await AppService.get("v1/organisation/keyValues?type=CUSTOMER");
      const list = Array.isArray(response) ? response : response?.data || [];
      setOrganisations(list);
    } catch (err) {
      console.error("Error fetching customer organisations:", err);
      setOrganisations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const fetchTopups = async (organisationId) => {
    setTopupsLoading(true);
    setTopupsError(null);
    try {
      const params = organisationId ? { organizationId: organisationId } : undefined;
      const response = await AppService.get("v1/wallet/transactions/topups", params ? { params } : undefined);
      setTopups(Array.isArray(response) ? response : response?.data || []);
    } catch (err) {
      console.error("Error fetching topups:", err);
      setTopups([]);
      setTopupsError("Failed to load top-up history");
    } finally {
      setTopupsLoading(false);
    }
  };

  const fetchCustomersSummary = async (overrides = {}) => {
    const page = overrides.page !== undefined ? overrides.page : customersPage;
    const pageSize = overrides.pageSize !== undefined ? overrides.pageSize : customersPageSize;
    const search = overrides.search !== undefined ? overrides.search : customersSearch;
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      const params = { page, pageSize };
      if (search && search.trim()) params.search = search.trim();
      const response = await AppService.get(
        "v1/admin/finance/overview/customer-payments-summary",
        { params }
      );
      setCustomers(response?.items || []);
      setCustomersTotal(response?.totalElements || 0);
    } catch (err) {
      console.error("Error fetching customer payments summary:", err);
      setCustomers([]);
      setCustomersTotal(0);
      setCustomersError("Failed to load customer summary");
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchTicketPayments = async (organisationId) => {
    setTicketPaymentsLoading(true);
    setTicketPaymentsError(null);
    try {
      const params = organisationId ? { organizationId: organisationId } : undefined;
      const response = await AppService.get(
        "v1/wallet/transactions/ticket-payment",
        params ? { params } : undefined
      );
      setTicketPayments(Array.isArray(response) ? response : response?.data || []);
    } catch (err) {
      console.error("Error fetching ticket payments:", err);
      setTicketPayments([]);
      setTicketPaymentsError("Failed to load ticket payment history");
    } finally {
      setTicketPaymentsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(updateScreenTitle("Customer Payments"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  useEffect(() => {
    fetchFinanceOverview();
    fetchCustomerOrganisations();
  }, []);

  useEffect(() => {
    fetchTopups(selectedOrganisationId);
    fetchTicketPayments(selectedOrganisationId);
  }, [selectedOrganisationId]);

  // Customers tab — initial load.
  useEffect(() => {
    fetchCustomersSummary({ page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input → triggers a fresh server fetch and resets to page 0
  // so the user lands on the first page of results for the new query.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customersSearchInput !== customersSearch) {
        setCustomersSearch(customersSearchInput);
        setCustomersPage(0);
        fetchCustomersSummary({ page: 0, search: customersSearchInput });
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersSearchInput]);

  // currentTab: 0 = Customers, 1 = Top-up History, 2 = Ticket Payment History.
  const activeRows = useMemo(() => {
    if (currentTab === 1) return topups;
    if (currentTab === 2) return ticketPayments;
    return [];
  }, [currentTab, topups, ticketPayments]);

  const activeLoading = currentTab === 1 ? topupsLoading
      : currentTab === 2 ? ticketPaymentsLoading : false;
  const activeError = currentTab === 1 ? topupsError
      : currentTab === 2 ? ticketPaymentsError : null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${pxToRem(55)})`,
        width: "100%",
      }}
    >
      {/* Tiles (same as Finance Overview first 3 cards) */}
      <Grid container spacing={2} mb={3} sx={{ alignItems: "stretch" }}>
        {/* Card 1: Total Bank Balance */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            sx={{
              p: 2,
              height: "100%",
              minHeight: 120,
              background: "linear-gradient(135deg, #1a7f8f 0%, #156b7b 100%)",
              color: "white",
              borderRadius: 2,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(26, 127, 143, 0.3)",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
                <Typography sx={{ fontSize: "0.75rem", color: "#ffffff", opacity: 0.85, mb: 1 }}>
                  Total Bank Balance
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ffffff" }}>
                    {formatAmount(overviewData.totalBankBalance)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: "#ffffff", opacity: 0.75, mt: 0.5 }}>
                    {overviewData.activeBankAccounts || 0} active accounts
                  </Typography>
                </Box>
              </Box>
              <AccountBalanceIcon sx={{ fontSize: 28, color: "#ffffff", opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Card 2: Ticket Payments (Paid + Pending) */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            sx={{
              p: 2,
              height: "100%",
              minHeight: 120,
              background: "linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)",
              color: "white",
              borderRadius: 2,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(26, 95, 107, 0.3)",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box sx={{ width: "100%" }}>
                <Typography sx={{ fontSize: "0.75rem", color: "#ffffff", opacity: 0.85, mb: 0.6 }}>
                  Ticket Payments
                </Typography>

                {/* Paid Section */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.3 }}>
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: "bold", color: "#4caf50" }}>
                        {formatAmount(overviewData.paidTicketPaymentsAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.6rem", color: "#ffffff", opacity: 0.75 }}>(Paid)</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.65rem", color: "#ffffff", opacity: 0.75 }}>
                      {overviewData.paidTicketCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* In Review Section — WAITING_FOR_APPROVAL: online payment
                    mid-gateway, distinct from settled and not-yet-initiated. */}
                <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.3 }}>
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: "bold", color: "#42a5f5" }}>
                        {formatAmount(overviewData.inReviewTicketPaymentsAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.6rem", color: "#ffffff", opacity: 0.75 }}>(Processing)</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.65rem", color: "#ffffff", opacity: 0.75 }}>
                      {overviewData.inReviewTicketCount || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* Pending Section */}
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.3 }}>
                      <Typography sx={{ fontSize: "1.1rem", fontWeight: "bold", color: "#ff9800" }}>
                        {formatAmount(overviewData.pendingTicketPaymentsAmount)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.6rem", color: "#ffffff", opacity: 0.75 }}>(Pending)</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.65rem", color: "#ffffff", opacity: 0.75 }}>
                      {overviewData.pendingTicketCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <PaymentIcon sx={{ fontSize: 28, color: "#ffffff", opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>

        {/* Card 3: Customer Wallet Balances */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            sx={{
              p: 2,
              height: "100%",
              minHeight: 120,
              background: "linear-gradient(135deg, #1f6b7a 0%, #1a5f6b 100%)",
              color: "white",
              borderRadius: 2,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(26, 95, 107, 0.3)",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", height: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
                <Typography sx={{ fontSize: "0.75rem", color: "#ffffff", opacity: 0.85, mb: 1 }}>
                  Customer Wallet Balances
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ffffff" }}>
                    {formatAmount(overviewData.totalCustomerWalletBalances)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: "#ffffff", opacity: 0.75, mt: 0.5 }}>
                    {overviewData.customersWithBalance || 0} customers with balance
                  </Typography>
                </Box>
              </Box>
              <AccountBalanceWalletIcon sx={{ fontSize: 28, color: "#ffffff", opacity: 0.6 }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {overviewLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading finance overview...
          </Typography>
        </Box>
      )}

      {overviewError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {overviewError}
        </Alert>
      )}

      {/* Tabs + Tables */}
      <Box sx={{ width: "100%", flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flexGrow: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          <Card sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ px: 2 }}>
                <Tab label="CUSTOMERS" />
                <Tab label="TOP-UP REQUEST HISTORY" />
                <Tab label="TICKET PAYMENT HISTORY" />
              </Tabs>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {currentTab === 0 ? (
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
                  {customersError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {customersError}
                    </Alert>
                  )}

                  {/* Search */}
                  <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "flex-end" }}>
                    <TextField
                      size="small"
                      placeholder="Search by organization name"
                      value={customersSearchInput}
                      onChange={(e) => setCustomersSearchInput(e.target.value)}
                      sx={{ minWidth: 320 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Sorted by highest unpaid amount first
                    </Typography>
                  </Box>

                  {/* Customers Table Header */}
                  <Box
                    display="flex"
                    alignItems="center"
                    columnGap={2}
                    sx={{ py: 1, px: 2, bgcolor: "grey.100", borderRadius: 1, mb: 1 }}
                  >
                    <Box flex={2.2}><Typography fontSize={12} fontWeight={600}>Organization</Typography></Box>
                    <Box flex={1.4} textAlign="right"><Typography fontSize={12} fontWeight={600}>Wallet Balance</Typography></Box>
                    <Box flex={1.6} textAlign="right"><Typography fontSize={12} fontWeight={600}>Paid (count)</Typography></Box>
                    <Box flex={1.6} textAlign="right"><Typography fontSize={12} fontWeight={600}>Processing (count)</Typography></Box>
                    <Box flex={1.6} textAlign="right"><Typography fontSize={12} fontWeight={600}>Unpaid (count)</Typography></Box>
                    <Box flex={1.2}><Typography fontSize={12} fontWeight={600}>Oldest Unpaid</Typography></Box>
                  </Box>

                  {/* Customers Table Rows */}
                  <Box sx={{ flexGrow: 1, overflow: "auto", ...noScrollBarStyle }}>
                    {customersLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                        <CircularProgress />
                      </Box>
                    ) : customers.length > 0 ? (
                      <Stack sx={{ overflow: "auto", flexGrow: 1 }}>
                        {customers.map((c) => (
                          <Box
                            key={c.organisationId}
                            sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              columnGap={2}
                              sx={{ py: 1.5, px: 2 }}
                            >
                              <Box flex={2.2}>
                                <Typography fontSize={13} fontWeight={600} noWrap>
                                  {c.organisationName || "—"}
                                </Typography>
                              </Box>
                              <Box flex={1.4} textAlign="right">
                                <Typography fontSize={13} fontWeight={600} color="primary.main">
                                  {formatAmount(c.walletBalance)}
                                </Typography>
                              </Box>
                              <Box flex={1.6} textAlign="right">
                                <Typography fontSize={13} fontWeight={600} color="#4caf50">
                                  {formatAmount(c.paidAmount)}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary">
                                  {c.paidCount || 0} ticket{(c.paidCount || 0) === 1 ? "" : "s"}
                                </Typography>
                              </Box>
                              <Box flex={1.6} textAlign="right">
                                <Typography fontSize={13} fontWeight={600} color="#42a5f5">
                                  {formatAmount(c.inReviewAmount)}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary">
                                  {c.inReviewCount || 0} ticket{(c.inReviewCount || 0) === 1 ? "" : "s"}
                                </Typography>
                              </Box>
                              <Box flex={1.6} textAlign="right">
                                <Typography fontSize={13} fontWeight={600} color="#ff9800">
                                  {formatAmount(c.unpaidAmount)}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary">
                                  {c.unpaidCount || 0} ticket{(c.unpaidCount || 0) === 1 ? "" : "s"}
                                </Typography>
                              </Box>
                              <Box flex={1.2}>
                                <Typography fontSize={12} color="text.secondary">
                                  {c.oldestUnpaidDate ? formatDate(c.oldestUnpaidDate) : "—"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                        <Typography color="text.secondary">No customers found.</Typography>
                      </Box>
                    )}
                  </Box>

                  <TablePagination
                    component="div"
                    count={customersTotal}
                    page={customersPage}
                    onPageChange={(_, newPage) => {
                      setCustomersPage(newPage);
                      fetchCustomersSummary({ page: newPage });
                    }}
                    rowsPerPage={customersPageSize}
                    onRowsPerPageChange={(e) => {
                      const next = parseInt(e.target.value, 10);
                      setCustomersPageSize(next);
                      setCustomersPage(0);
                      fetchCustomersSummary({ page: 0, pageSize: next });
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                  />
                </Box>
              ) : (
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
                {activeError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {activeError}
                  </Alert>
                )}

                {/* Filters */}
                <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "flex-end" }}>
                  <FormControl size="small" sx={{ minWidth: 320 }} disabled={orgLoading}>
                    <InputLabel id="org-filter-label">Filter by Customer</InputLabel>
                    <Select
                      labelId="org-filter-label"
                      value={selectedOrganisationId}
                      onChange={(e) => setSelectedOrganisationId(e.target.value)}
                      label="Filter by Customer"
                    >
                      <MenuItem value="">All Customers</MenuItem>
                      {organisations.map((org) => (
                        <MenuItem key={org.id || org.value} value={org.id || org.value}>
                          {org.name || org.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Table Header */}
                <Box
                  display="flex"
                  alignItems="center"
                  columnGap={2}
                  sx={{ py: 1, px: 2, bgcolor: "grey.100", borderRadius: 1, mb: 1 }}
                >
                  <Box flex={2}><Typography fontSize={12} fontWeight={600}>Txn #</Typography></Box>
                  <Box flex={1}><Typography fontSize={12} fontWeight={600}>Date</Typography></Box>
                  <Box flex={2}><Typography fontSize={12} fontWeight={600}>Organization</Typography></Box>
                  <Box flex={1} textAlign="right"><Typography fontSize={12} fontWeight={600}>Amount</Typography></Box>
                  <Box flex={1}><Typography fontSize={12} fontWeight={600}>Status</Typography></Box>
                  <Box flex={1}><Typography fontSize={12} fontWeight={600}>Source</Typography></Box>
                  <Box flex={3}><Typography fontSize={12} fontWeight={600}>Description</Typography></Box>
                </Box>

                {/* Rows */}
                <Box sx={{ flexGrow: 1, overflow: "auto", ...noScrollBarStyle }}>
                  {activeLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                      <CircularProgress />
                    </Box>
                  ) : activeRows.length > 0 ? (
                    <Stack sx={{ overflow: "auto", flexGrow: 1 }}>
                      {activeRows.map((row) => (
                        <Box
                          key={row.id || uuid()}
                          sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1, overflow: "hidden" }}
                        >
                          <Box
                            display="flex"
                            alignItems="flex-start"
                            columnGap={2}
                            sx={{ py: 1.5, px: 2 }}
                          >
                            <Box flex={2}>
                              <Typography fontSize={12} color="text.secondary">
                                {row.transactionNumber || "—"}
                              </Typography>
                            </Box>

                            <Box flex={1}>
                              <Typography fontSize={13}>
                                {formatDate(row.createdDate)}
                              </Typography>
                            </Box>

                            <Box flex={2}>
                              <Typography fontSize={13} color="text.secondary" noWrap>
                                {row.organizationName || "—"}
                              </Typography>
                            </Box>

                            <Box flex={1} textAlign="right">
                              <Typography fontSize={14} fontWeight={600} color="primary.main">
                                {row.amount ? Number(row.amount).toFixed(2) : "0.00"} AED
                              </Typography>
                            </Box>

                            <Box flex={1}>
                              <Typography fontSize={12} fontWeight={500} color="text.secondary">
                                {row.status ? String(row.status).replace(/_/g, " ") : "—"}
                              </Typography>
                            </Box>

                            <Box flex={1}>
                              <Typography fontSize={12} fontWeight={500} color={row.paymentSource === "WALLET" ? "text.secondary" : "primary.main"}>
                                {row.paymentSource === "WALLET" ? "Wallet" : row.paymentSource || "—"}
                              </Typography>
                            </Box>

                            <Box flex={3}>
                              <Typography
                                fontSize={12}
                                color="text.secondary"
                                sx={{
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  lineHeight: 1.4
                                }}
                              >
                                {row.description || "—"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
                      No history found
                    </Typography>
                  )}
                </Box>
              </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerPaymentsPage;
